import {
  classifySurface,
  normalizeError,
  type CrashEvent,
  type Session,
  type SupportedSurface,
} from "@panelgenie/domain";
import type {
  CrashReporter,
  ReportCrashInput,
  ReportCrashOutcome,
  StartSessionInput,
} from "./CrashReporter.js";

/**
 * Phase 14c — browser `CrashReporter` adapter.
 *
 * Ships normalized crash events to a Sentry-compatible HTTP endpoint
 * via the `store` envelope: a single `POST <dsnEndpoint>` per event,
 * `Content-Type: application/json`, body shaped like Sentry's event
 * payload (`{ event_id, timestamp, level, exception, release, tags,
 * extra }`). The wire shape is intentionally vendor-neutral: GlitchTip
 * accepts the same envelope (its API mirrors Sentry's); Axiom's
 * `ingest` endpoint accepts the same JSON with a different URL +
 * auth header.
 *
 * `dsn` is parsed at construction time. When the DSN is absent (the
 * common dev / preview-build case), the adapter falls back to a
 * console-log sink and never reaches the network — the
 * release-gate dashboard placeholder is the only vendor-visible
 * surface and is not yet wired (per the slice's "stub the client and
 * document the deferred live-wire-up" carry-forward).
 *
 * This is the only module under `apps/web/src/` that reaches for
 * `navigator.userAgent` outside the seam allow-list — by construction.
 */

export type BrowserCrashReporterOptions = {
  /**
   * Crash-reporter vendor DSN. The seam parses the URL once at
   * construction; an empty / unparsable DSN falls back to the
   * console-log sink so a missing env var doesn't break the boot.
   *
   * Example: `https://<key>@<host>/<project>` (Sentry / GlitchTip
   * format). Axiom uses a different URL + an `Authorization: Bearer
   * <token>` header; pass a DSN-shaped wrapper at deploy time.
   */
  readonly dsn?: string | undefined;
  /**
   * Release identifier (build sha or version tag). Sentry's dashboard
   * groups events by release so a hotfix's crashes drop off the
   * rolling window after deploy.
   */
  readonly release: string;
  /**
   * Optional fetch impl override (tests + SSR). Defaults to the
   * global `fetch`.
   */
  readonly fetchImpl?: typeof fetch;
  /**
   * Optional wall-clock instant generator (tests). Defaults to
   * `new Date().toISOString()`.
   */
  readonly now?: () => string;
  /**
   * Optional event-id generator (tests). Defaults to
   * `crypto.randomUUID()` — fall back to a `Math.random()`-based id
   * if `crypto` is absent.
   */
  readonly nextEventId?: () => string;
  /**
   * Optional console sink (tests). Defaults to `console.error`.
   */
  readonly consoleSink?: (line: string) => void;
};

type ParsedDsn = {
  readonly endpoint: string;
  readonly publicKey: string;
};

/**
 * Parse a Sentry-format DSN: `https://<key>@<host>/<project>`. Returns
 * `undefined` when the input is empty / unparsable so the adapter
 * falls back to the console sink without throwing at boot.
 *
 * Pure; exported so tests can pin the shape.
 */
export const parseDsn = (raw: string | undefined): ParsedDsn | undefined => {
  if (raw === undefined || raw.trim().length === 0) return undefined;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
  if (url.username.length === 0) return undefined;
  const projectId = url.pathname.replace(/^\//, "");
  if (projectId.length === 0) return undefined;
  // The standard Sentry store endpoint is `<scheme>://<host>/api/<project>/store/`.
  const endpoint = `${url.protocol}//${url.host}/api/${projectId}/store/`;
  return { endpoint, publicKey: url.username };
};

export class BrowserCrashReporter implements CrashReporter {
  private readonly release: string;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => string;
  private readonly nextEventId: () => string;
  private readonly consoleSink: (line: string) => void;
  private readonly parsedDsn: ParsedDsn | undefined;
  private activeSession: Session | undefined;

  constructor(options: BrowserCrashReporterOptions) {
    this.release = options.release;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    this.now = options.now ?? (() => new Date().toISOString());
    this.nextEventId = options.nextEventId ?? defaultEventIdGenerator;
    this.consoleSink = options.consoleSink ?? ((line) => console.error(line));
    this.parsedDsn = parseDsn(options.dsn);
  }

  startSession(input: StartSessionInput): void {
    this.activeSession = {
      sessionId: input.sessionId,
      startedAt: input.startedAt,
      crashedAt: undefined,
      surface: input.surface,
      release: input.release,
    };
  }

  async reportCrash(input: ReportCrashInput): Promise<ReportCrashOutcome> {
    if (this.activeSession === undefined) {
      return {
        kind: "skipped",
        reason: "no active session — call startSession before reportCrash",
      };
    }
    const eventId = this.nextEventId();
    const capturedAt = this.now();
    const event: CrashEvent = {
      eventId,
      capturedAt,
      severity: input.severity,
      error: normalizeError(input.thrown),
      sessionId: this.activeSession.sessionId,
      release: this.activeSession.release,
      surface: this.activeSession.surface,
      userAgent: this.activeSession.surface === "unknown" ? "" : "",
      context: input.context ?? {},
    };
    if (
      (input.severity === "fatal" || input.severity === "error") &&
      this.activeSession.crashedAt === undefined
    ) {
      this.activeSession = {
        ...this.activeSession,
        crashedAt: capturedAt,
      };
    }
    if (this.parsedDsn === undefined) {
      // No DSN — fall back to console.error so the dev surface still
      // sees the event without a vendor wire-up.
      try {
        this.consoleSink(
          `[crash-reporter:stub] ${JSON.stringify(serializeEvent(event))}`,
        );
      } catch {
        // Even the console sink failed — swallow silently per the
        // "MUST NOT throw" contract on a transport failure.
      }
      return { kind: "ok", eventId };
    }
    // Ship to the vendor. Failure to ship MUST NOT throw — the seam
    // is already inside the error boundary.
    try {
      const response = await this.fetchImpl(this.parsedDsn.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Sentry-Auth": `Sentry sentry_version=7, sentry_key=${this.parsedDsn.publicKey}, sentry_client=panelgenie/1.0`,
        },
        body: JSON.stringify(serializeEvent(event)),
        // Best-effort; do not retain a connection past the unload
        // event the user is firing alongside the crash.
        keepalive: true,
      });
      if (!response.ok) {
        return {
          kind: "failure",
          message: `crash-reporter HTTP ${response.status}`,
        };
      }
      return { kind: "ok", eventId };
    } catch (err) {
      return {
        kind: "failure",
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  currentSessionId(): string | undefined {
    return this.activeSession?.sessionId;
  }
}

/**
 * Build a `CrashEvent` from current platform globals + a thrown
 * value. Browser-only helper used by the smoke-test script. Tests
 * inject the user-agent explicitly.
 */
export const buildBrowserSessionStart = (input: {
  readonly sessionId: string;
  readonly startedAt: string;
  readonly release: string;
  readonly userAgent: string;
}): StartSessionInput => {
  const surface: SupportedSurface = classifySurface(input.userAgent);
  return {
    sessionId: input.sessionId,
    startedAt: input.startedAt,
    release: input.release,
    userAgent: input.userAgent,
    surface,
  };
};

/**
 * Serialize a `CrashEvent` to the Sentry-store wire shape. Exported
 * so the smoke-test script can pin the JSON payload.
 */
export const serializeEvent = (event: CrashEvent) => ({
  event_id: event.eventId.replaceAll("-", ""),
  timestamp: event.capturedAt,
  level: event.severity,
  release: event.release,
  platform: "javascript",
  exception: {
    values: [
      {
        type: event.error.name,
        value: event.error.message,
        stacktrace: event.error.stack !== undefined
          ? { frames: stackFrames(event.error.stack) }
          : undefined,
      },
    ],
  },
  tags: {
    "panelgenie.surface": event.surface,
    "panelgenie.session_id": event.sessionId,
  },
  extra: event.context,
  contexts: {
    browser: {
      userAgent: event.userAgent,
    },
  },
});

/**
 * Parse an `Error.stack` into Sentry's frame array shape. Best-
 * effort: each non-empty line becomes one frame's `function` slot.
 * The vendor's stack-trace UI doesn't depend on column-perfect
 * frames; source-map upload (deferred per the carry-forward) is
 * what makes the frames legible.
 */
const stackFrames = (stack: string) =>
  stack
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 50)
    .reverse()
    .map((line) => ({ function: line }));

const defaultEventIdGenerator = (): string => {
  // Sentry's `event_id` is a 32-char lowercase hex string (UUID v4
  // without dashes). The seam emits hyphens too; the wire shape
  // serializer strips them.
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID !== undefined) return c.randomUUID();
  } catch {
    // fall through
  }
  // Math.random()-based fallback. Never reached in a real browser; the
  // unit tests inject `nextEventId` so they don't hit this path.
  let s = "";
  for (let i = 0; i < 32; i++) {
    s += Math.floor(Math.random() * 16).toString(16);
  }
  return s;
};
